/* @flow */
import Backbone from "backbone";
import Backstage from "nexia_mobile/backstage";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import Url from "nexia_mobile/url";
import type {
  BackstageUrl,
  Link,
  ServerResponse,
  Url as UrlType,
} from "nexia_mobile/api/types";

// A JSON string that becomes ParsedType when put through JSON.parse.
// Flow just treats it as a string, so it's just for documentative purposes.
// eslint-disable-next-line no-unused-vars
type JSONString<ParsedType> = string;

// Options having to do with ajax
type AjaxBaseOptions = {
  dataType: string,
  contentType: string,
  method?:
    | "GET"
    | "HEAD"
    | "POST"
    | "PUT"
    | "DELETE"
    | "CONNECT"
    | "OPTIONS"
    | "TRACE"
    | "PATCH", // defaults to "POST" if omitted
  url: UrlType,
};

// Options having to do with ajax, that can be passed into followLink
type InputAjaxOptions<RequestBody> = AjaxBaseOptions & {
  data: RequestBody | JSONString<RequestBody>,
};

// Options having to do with ajax,that followLink will pass into Backbone.ajax
type ParsedAjaxOptions<RequestBody> = AjaxBaseOptions & {
  data: JSONString<RequestBody>,
};

// Options that are specific to followLink itself and do not affect the behavior
// of any of the things that the options get forwarded to
type FollowLinkSpecificOptions = {
  // Whether to fetch the model after performing the action.
  fetchModelWhenDone: boolean,
  // Whether to update the model using the response obtained from following the
  // link.
  updateModelWithResponse: boolean,
  // Whether to trigger backbone request/sync/error events on the model.
  silent: boolean,
};

type ForwardedBackstageOptions = {
  maxRequestsCount: number,
  maxRequestsReached?: ?() => void,
};

// Options that can be passed into followLink. Note that $Shape makes all the
// entries optional.
type InputFollowLinkOptions<RequestBody> = $Shape<
  ForwardedBackstageOptions &
    InputAjaxOptions<RequestBody> &
    FollowLinkSpecificOptions
>;

// What followLink parses its input options into and then forwards along to all
// the things it calls internally (Backstage, Backbone.ajax, Backbone model
// events)
type ParsedFollowLinkOptions<RequestBody> = ForwardedBackstageOptions &
  ParsedAjaxOptions<RequestBody> &
  FollowLinkSpecificOptions;

const getBackstagePollingPath = (
  response: BackstageUrl | ServerResponse<BackstageUrl> | any
): ?UrlType => {
  if (typeof response !== "object" || response == null) {
    // response is not an object
    return null;
  }

  if (typeof response.polling_path === "string") {
    // response is a BackstageUrl
    return response.polling_path;
  }

  if (
    typeof response.result === "object" &&
    response.result != null &&
    typeof response.result.polling_path === "string"
  ) {
    // response is a ServerResponse<BackstageUrl>
    return response.result.polling_path;
  }

  return null;
};

const ajaxWithBackstageSupport = <RequestBody, ResponseBody>(
  options: ParsedFollowLinkOptions<RequestBody>,
  // This next argument is not used at runtime, but is used by flow for type
  // validation.
  // eslint-disable-next-line no-unused-vars
  link: Link<RequestBody, ResponseBody>

  // TODO: This function returns Promise<ResponseBody> when the server responds
  // with a ResponseBody, but it returns Promise<any> when the server responds
  // with a ServerResponse<BackstageUrl>, because we have not made any guarantees
  // on the Backstage success options type serverside. This lack of a guarantee
  // leaks out into the return type of followLink. If we went through the places
  // where the server calls Backstage.success on a uid that came from a mobile
  // Link, and made sure to always include a ResponseBody in the options, then the
  // return type from this function could be Promise<ResponseBody>, which is a lot
  // more useful.
): Promise<any> => {
  return new Promise((resolve, reject) => {
    Backbone.ajax(options).then(
      (response: ResponseBody | ServerResponse<BackstageUrl> | any) => {
        const pollingPath = getBackstagePollingPath(response);
        if (pollingPath) {
          new Backstage(pollingPath, {
            success: (data) => {
              // TODO: Why is this data.options || data? The server should
              // always respond with a BackstageResponse of some
              // sort; are we ever *not* receiving a BackstageResponse here?
              resolve(data.options || data);
            },
            error: (errors, xhr) => {
              reject(Object.assign(xhr, { serverErrors: errors }));
            },
            maxRequestsCount: options.maxRequestsCount,
            maxRequestsReached: options.maxRequestsReached,
          }).startPolling();
        } else {
          resolve(response);
        }
      },
      (xhr) => {
        reject(xhr);
      }
    );
  });
};

function followLink<RequestBody, ResponseBody>(
  model: ?(Backbone.Model & Backbone.Events),
  link: Link<RequestBody, ResponseBody>,
  passedOptions: InputFollowLinkOptions<RequestBody>
): Promise<any> {
  let dataForURITemplate: ?RequestBody;
  if (link.templated) {
    if (passedOptions.data != null && typeof passedOptions.data === "object") {
      dataForURITemplate = passedOptions.data;
    }
    if (dataForURITemplate == null) {
      if (typeof model === "object" && model != null) {
        // Flow points out here that a model's attributes might contain
        // properties which aren't in the URI template. That's true, but that's
        // how the app works. Not gonna try to fix it right now
        // $FlowIgnore
        dataForURITemplate = model.attributes;
      } else {
        throw new Error(
          "Attempted to follow a templated link without any data with which " +
            "to expand the template"
        );
      }
    }
  }

  const { data: passedData, ...restOfPassedOptions } = passedOptions;

  let parsedData: string = "";
  if (typeof passedData === "string") {
    parsedData = passedData;
  } else if (passedData) {
    parsedData = JSON.stringify(passedData);
  }

  const options: ParsedFollowLinkOptions<RequestBody> = {
    // Ajax Option Defaults
    data: parsedData,
    dataType: "json",
    contentType: "application/json; charset=utf-8",
    method: "POST", // default unless overridden by link.method

    // Backbone Option Defaults
    maxRequestsCount: 60,

    // followLink-specific option defaults
    fetchModelWhenDone: false,
    updateModelWithResponse: false,
    silent: false,

    // User overrides of defaults, or options that they want to pass into
    // ajax or Backstage
    ...restOfPassedOptions,

    ...(link.method && { method: link.method }),
    url: link.templated
      ? Url.expandTemplate(link.href, dataForURITemplate)
      : link.href,
  };

  const trigger = (...args) => {
    if (!options.silent && typeof model === "object" && model != null) {
      model.trigger(...args);
    }
  };

  return new Promise((resolve, reject) => {
    trigger("request", model, {}, options);
    Dispatch("DISABLE_LOCATION_POLLING");

    const finishWithError = (xhr) => {
      trigger("error", model, xhr, options);
      Dispatch("ENABLE_LOCATION_POLLING");
      reject();
    };

    const finishWithData = (data) => {
      const parsedResponseData = (data || {}).result || data;
      trigger("sync", model, parsedResponseData, options);
      Dispatch("ENABLE_LOCATION_POLLING");
      resolve(parsedResponseData);
    };

    const finishWithFetch = () => {
      if (typeof model === "object" && model != null) {
        model.fetch().then(finishWithData, finishWithError);
      } else {
        throw new Error(
          "Attempted to fetch model after following link (due to received " +
            "fetchModelWhenDone option), but fetchLink was not called with a " +
            "model."
        );
      }
    };

    ajaxWithBackstageSupport(options, link).then(
      (data) => {
        if (options.fetchModelWhenDone) {
          finishWithFetch();
        } else {
          if (options.updateModelWithResponse) {
            if (typeof model === "object" && model != null) {
              model.set(model.parse(data));
            } else {
              throw new Error(
                "Attempted to update model with response data after following " +
                  "link (due to received updateModelWithResponse option), but " +
                  "fetchLink was not called with a model."
              );
            }
          }

          finishWithData(data);
        }
      },
      (xhr) => {
        finishWithError(xhr);
      }
    );
  });
}

export default followLink;



// WEBPACK FOOTER //
// ./src/models/followLink.js